import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Offcanvas, Form, Row, Col } from 'react-bootstrap';
import $ from 'jquery';
import 'datatables.net-bs5';
import "datatables.net-responsive/js/dataTables.responsive.min.js";
import { useHasPermission, PERMISSIONS } from '../../../utils/permissions';
import PermissionGate from '../../common/PermissionGate';
import { toast } from 'react-toastify';

function PackagingMaterialPOTable({ orders, canEdit = false, canDelete = false, canView = true }) {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        vendor: '',
        broker: '',
        status: '',
        paymentStatus: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [
        brokers,
        //  setBrokers
    ] = useState([]);

    const canViewDocuments = useHasPermission(PERMISSIONS.PO_DOC_ADD);
    const canViewPayments = useHasPermission(PERMISSIONS.PO_PAYMENT_ADD);
    const canViewGRN = useHasPermission(PERMISSIONS.GRN_CREATE);

    useEffect(() => {
        if (orders && orders.length > 0) {
            setFilteredOrders(orders);
            const uniqueVendors = orders.filter(o => o.supplier).map(o => o.supplier);
            setVendors([...new Map(uniqueVendors.map(v => [v._id, v])).values()]);
        }
    }, [orders]);

    useEffect(() => {
        if (filteredOrders && filteredOrders.length >= 0) {
            const $table = $(tableRef.current);

            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }

            setTimeout(() => {
                dataTableRef.current = $table.DataTable({
                    responsive: true,
                    destroy: true,
                    data: filteredOrders,
                    columns: [
                        {
                            data: null,
                            render: (data, type, row, meta) => meta.row + 1,
                            title: "SN",
                            width: "50px"
                        },
                        {
                            data: 'poNumber',
                            title: "Order No"
                        },
                        {
                            data: 'orderDate',
                            title: "Order Date",
                            render: (data) => new Date(data).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })
                        },
                        {
                            data: 'items',
                            title: "Items",
                            render: (data) => {
                                if (!data || data.length === 0) return 'No items';
                                return data.map(item =>
                                    `${item.packingMaterial?.name || item.packingMaterial?.alias || 'N/A'} (${item.quantity} ${item.unit})`
                                ).join('<br>');
                            },
                            orderable: false
                        },
                        {
                            data: null,
                            title: "Supplier",
                            render: (data, type, row) => {
                                let html = `<div>${row.supplier?.business_name || 'N/A'}</div>`;
                                return html;
                            }
                        },
                        {
                            data: 'status',
                            title: "Status",
                            render: (data) => `<span class="badge text-bg-${getOrderStatusColor(data)}">${data}</span>`
                        },
                        {
                            data: 'paymentStatus',
                            title: "Payment",
                            render: (data) => `<span class="badge text-bg-${getPaymentStatusColor(data)}">${data}</span>`
                        },
                        {
                            data: '_id',
                            title: "Actions",
                            render: (data, type, row) => {
                                let actions = '';

                                if (canView) {
                                    actions += `<a class="btn btn-sm btn-info me-1" href="/packaging-material-po/${data}" title="View Details">
                                        <i class="fas fa-eye"></i>
                                    </a>`;
                                }

                                if (canEdit) {
                                    actions += `<a class="btn btn-sm btn-warning me-1" href="/edit-packaging-material-po/${data}" title="Edit Order">
                                        <i class="fas fa-edit"></i>
                                    </a>`;
                                }

                                return actions;
                            },
                            className: 'text-end',
                            orderable: false,
                            width: canEdit ? "120px" : "80px"
                        }
                    ],
                    layout: {
                        bottomEnd: {
                            paging: {
                                firstLast: false
                            }
                        }
                    }
                });
            }, 100);
        }

        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
        };
    }, [filteredOrders, canView, canEdit, canDelete, canViewDocuments, canViewPayments, canViewGRN]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        let filtered = orders || [];

        if (filters.vendor) {
            filtered = filtered.filter(order => order.supplier?._id === filters.vendor);
        }
        if (filters.broker) {
            filtered = filtered.filter(order => order.brokerId?._id === filters.broker);
        }
        if (filters.status) {
            filtered = filtered.filter(order => order.status === filters.status);
        }
        if (filters.paymentStatus) {
            filtered = filtered.filter(order => order.paymentStatus === filters.paymentStatus);
        }
        if (filters.dateFrom) {
            filtered = filtered.filter(order =>
                new Date(order.orderDate) >= new Date(filters.dateFrom)
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(order =>
                new Date(order.orderDate) <= new Date(filters.dateTo)
            );
        }

        setFilteredOrders(filtered);
        setShowFilters(false);
    };

    const clearFilters = () => {
        setFilters({
            vendor: '',
            broker: '',
            status: '',
            paymentStatus: '',
            dateFrom: '',
            dateTo: ''
        });
        setFilteredOrders(orders || []);
    };

    const exportToCSV = () => {
        if (!canView) {
            toast.error("You don't have permission to export purchase orders");
            return;
        }

        const csvContent = [
            ['Order No', 'Order Date', 'Supplier', 'Status', 'Total Amount'],
            ...filteredOrders.map(order => [
                order.poNumber,
                new Date(order.orderDate).toLocaleDateString(),
                order.supplier?.business_name || 'N/A',
                order.status,
                order.totalAmount || 0
            ])
        ];

        const csvString = csvContent.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `packaging_material_pos_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (!orders || orders.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p className="text-muted">No packaging material purchase orders found.</p>
                <PermissionGate require={PERMISSIONS.PM_PO_CREATE}>
                    <Link to="/new-packaging-material-po" className="btn btn-primary">
                        <i className="fas fa-plus me-2"></i>Create First Packing Material PO
                    </Link>
                </PermissionGate>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <div className="d-flex align-items-center gap-3">
                    <small className="text-muted">
                        Showing {filteredOrders.length} of {orders?.length || 0} orders
                    </small>
                    {(canEdit || canDelete) && (
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={exportToCSV}
                                title="Export to CSV"
                            >
                                <i className="fas fa-download me-1"></i>Export
                            </Button>
                        </div>
                    )}
                </div>
                <Button variant="outline-primary" onClick={() => setShowFilters(true)}>
                    <i className="fas fa-filter me-2"></i>Filters
                </Button>
            </div>

            <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filter Purchase Orders</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor</Form.Label>
                            <Form.Select
                                value={filters.vendor}
                                onChange={(e) => handleFilterChange('vendor', e.target.value)}
                            >
                                <option value="">All Vendors</option>
                                {vendors.map(vendor => (
                                    <option key={vendor._id} value={vendor._id}>
                                        {vendor.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Broker</Form.Label>
                            <Form.Select
                                value={filters.broker}
                                onChange={(e) => handleFilterChange('broker', e.target.value)}
                            >
                                <option value="">All Brokers</option>
                                {brokers.map(broker => (
                                    <option key={broker._id} value={broker._id}>
                                        {broker.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Created">Created</option>
                                <option value="Partially Delivered">Partially Delivered</option>
                                <option value="Delivered">Delivered</option>
                                <option value="Closed">Closed</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Payment Status</Form.Label>
                            <Form.Select
                                value={filters.paymentStatus}
                                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                            >
                                <option value="">All Payment Status</option>
                                <option value="Unpaid">Unpaid</option>
                                <option value="Partially Paid">Partially Paid</option>
                                <option value="Paid">Paid</option>
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-grid gap-2">
                            <Button variant="primary" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>

            <div className="table-responsive">
                <table
                    ref={tableRef}
                    className="table table-bordered table-hover table-sm display responsive nowrap"
                    id="ordersTable"
                    style={{ width: '100%' }}
                >
                    <thead className="table-light">
                        <tr>
                            <th>SN</th>
                            <th>Order No</th>
                            <th>Order Date</th>
                            <th>Items</th>
                            <th>Vendor</th>
                            <th>Status</th>
                            <th>Payment</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                    </tbody>
                </table>
            </div>
        </>
    );
}

function getOrderStatusColor(status) {
    switch (status) {
        case 'Created':
            return 'warning';
        case 'Partially Delivered':
            return 'secondary';
        case 'Delivered':
            return 'primary';
        case 'Closed':
            return 'success';
        default:
            return 'light';
    }
}

function getPaymentStatusColor(status) {
    switch (status) {
        case 'Unpaid':
            return 'danger';
        case 'Partially Paid':
            return 'warning';
        case 'Paid':
            return 'success';
        default:
            return 'light';
    }
}

export default PackagingMaterialPOTable;
